import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePermission } from '../../../src/utils/permissions';
import { useAuth } from '../../../src/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  ClipboardCheck,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Eye,
  CheckCircle,
  Clock,
  X,
  Save,
  Send,
  Truck,
  FileText,
  Building,
  Package,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { useInventoryItems, useWarehouses } from '../../../src/hooks/queries/use-inventory-items';
import { useMaterialRequisitions } from '../../../src/hooks/queries/use-material-requisitions';
import {
  procurementService,
  MaterialRequisitionBackend,
  MaterialRequisitionItemBackend,
  InventoryItemBackend,
} from '../../../src/services/procurement.service';
import { queryKeys } from '../../../src/lib/query-keys';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';

// ─── Status helpers ───────────────────────────────────────────────────────────

function getStatusBadge(status: string) {
  switch (status.toLowerCase()) {
    case 'pending': return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    case 'approved': return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    case 'issued': return <Badge className="bg-blue-100 text-blue-800 border-blue-200"><Truck className="h-3 w-3 mr-1" />Issued</Badge>;
    case 'rejected': return <Badge className="bg-red-100 text-red-800 border-red-200"><X className="h-3 w-3 mr-1" />Rejected</Badge>;
    case 'cancelled': return <Badge variant="secondary"><X className="h-3 w-3 mr-1" />Cancelled</Badge>;
    default: return <Badge variant="secondary">{status}</Badge>;
  }
}

// ─── Create / Edit Modal ──────────────────────────────────────────────────────

interface RequisitionLineItem {
  inventory_item_id: string;
  requested_quantity: string;
  notes: string;
}

interface RequisitionModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: 'add' | 'view';
  requisition?: MaterialRequisitionBackend;
  requisitionItems?: MaterialRequisitionItemBackend[];
  orgId: string;
  projects: { project_id: string; name: string; project_manager_id?: string }[];
  inventoryItems: InventoryItemBackend[];
  warehouses: { warehouse_id: string; warehouse_name: string; is_active?: boolean }[];
  onSuccess: () => void;
}

function RequisitionModal({
  open, onOpenChange, mode, requisition, requisitionItems = [],
  orgId, projects, inventoryItems, warehouses, onSuccess
}: RequisitionModalProps) {
  const isView = mode === 'view';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    projectId: requisition?.project_id ?? '',
    requiredByDate: requisition?.required_by_date ?? '',
    deliveryWarehouseId: (requisition as any)?.delivery_warehouse_id ?? '',
    deliveryAddress: (requisition as any)?.delivery_address ?? '',
    deliveryNotes: (requisition as any)?.delivery_notes ?? '',
    notes: requisition?.notes ?? '',
  });
  const [lineItems, setLineItems] = useState<RequisitionLineItem[]>(
    requisitionItems.length > 0
      ? requisitionItems.map(i => ({
          inventory_item_id: i.inventory_item_id,
          requested_quantity: String(i.requested_quantity),
          notes: i.notes ?? '',
        }))
      : [{ inventory_item_id: '', requested_quantity: '', notes: '' }]
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const inventoryMap = useMemo(() =>
    Object.fromEntries(inventoryItems.map(i => [i.inventory_item_id, i])),
    [inventoryItems]
  );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.projectId) e.projectId = 'Project is required';
    if (!formData.requiredByDate) e.requiredByDate = 'Required date is required';
    const validLines = lineItems.filter(l => l.inventory_item_id && l.requested_quantity);
    if (validLines.length === 0) e.items = 'At least one item with quantity is required';
    lineItems.forEach((l, idx) => {
      if (l.inventory_item_id && (!l.requested_quantity || Number(l.requested_quantity) <= 0)) {
        e[`qty_${idx}`] = 'Enter a valid quantity';
      }
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const addLine = () => setLineItems(prev => [...prev, { inventory_item_id: '', requested_quantity: '', notes: '' }]);
  const removeLine = (idx: number) => setLineItems(prev => prev.filter((_, i) => i !== idx));
  const updateLine = (idx: number, field: keyof RequisitionLineItem, value: string) => {
    setLineItems(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const reqNumber = await procurementService.getNextRequisitionNumber(orgId);
      const mr = await procurementService.createMaterialRequisition({
        organization_id: orgId,
        project_id: formData.projectId,
        requisition_number: reqNumber,
        required_by_date: formData.requiredByDate || undefined,
        delivery_warehouse_id: formData.deliveryWarehouseId || undefined,
        delivery_address: formData.deliveryAddress || undefined,
        delivery_notes: formData.deliveryNotes || undefined,
        notes: formData.notes || undefined,
      });

      const validLines = lineItems.filter(l => l.inventory_item_id && l.requested_quantity);
      for (const line of validLines) {
        await procurementService.addRequisitionItem(mr.requisition_id, {
          organization_id: orgId,
          inventory_item_id: line.inventory_item_id,
          requested_quantity: Number(line.requested_quantity),
          notes: line.notes || undefined,
        });
      }

      showSuccess({ title: 'Requisition Created', message: `${reqNumber} has been submitted` });
      const project = projects.find(p => p.project_id === formData.projectId);
      if (project?.project_manager_id) {
        notificationService.materialRequisitionCreated(project.project_manager_id, [], reqNumber, project.name);
      }
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Failed to Create Requisition', message: err?.message ?? 'An error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            {isView ? `Requisition — ${requisition?.requisition_number}` : 'Create Material Requisition'}
          </DialogTitle>
          <DialogDescription>
            {isView ? 'Material requisition details' : 'Request materials for project site usage'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Project & Dates */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Building className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Project Information</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project *</Label>
                {isView ? (
                  <Input
                    value={projects.find(p => p.project_id === (requisition?.project_id ?? formData.projectId))?.name ?? formData.projectId}
                    readOnly className="bg-muted"
                  />
                ) : (
                  <>
                    <Select value={formData.projectId} onValueChange={(v) => setFormData({ ...formData, projectId: v })}>
                      <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {errors.projectId && <p className="text-sm text-destructive">{errors.projectId}</p>}
                  </>
                )}
              </div>
              <div className="space-y-2">
                <Label>Required By Date *</Label>
                <Input
                  type="date"
                  value={formData.requiredByDate}
                  onChange={(e) => setFormData({ ...formData, requiredByDate: e.target.value })}
                  readOnly={isView}
                  className={isView ? 'bg-muted' : ''}
                />
                {errors.requiredByDate && <p className="text-sm text-destructive">{errors.requiredByDate}</p>}
              </div>
            </div>
            {isView && requisition && (
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><span className="font-medium">Status:</span> <span className="ml-1">{requisition.status}</span></div>
                <div><span className="font-medium">Requested:</span> <span className="ml-1">{requisition.request_date ? new Date(requisition.request_date).toLocaleDateString() : '—'}</span></div>
              </div>
            )}

            {/* Delivery Details */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-primary" />
                <h4 className="font-medium text-sm">Delivery Details</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Deliver to Warehouse</Label>
                  <Select
                    value={formData.deliveryWarehouseId}
                    onValueChange={(v) => setFormData({ ...formData, deliveryWarehouseId: v })}
                    disabled={isView}
                  >
                    <SelectTrigger><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                    <SelectContent>
                      {warehouses.filter(w => w.warehouse_id && w.is_active !== false).map(w => (
                        <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Delivery Address</Label>
                  <Input
                    value={formData.deliveryAddress}
                    onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                    placeholder="Site address or GPS"
                    readOnly={isView}
                    className={isView ? 'bg-muted' : ''}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Delivery Notes</Label>
                <Input
                  value={formData.deliveryNotes}
                  onChange={(e) => setFormData({ ...formData, deliveryNotes: e.target.value })}
                  placeholder="Access instructions, contact person, etc."
                  readOnly={isView}
                  className={isView ? 'bg-muted' : ''}
                />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-primary" />
                <h4 className="font-medium">Requested Materials</h4>
              </div>
              {!isView && (
                <Button variant="outline" size="sm" onClick={addLine}>
                  <Plus className="h-4 w-4 mr-2" />Add Item
                </Button>
              )}
            </div>
            {errors.items && <p className="text-sm text-destructive">{errors.items}</p>}

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Notes</TableHead>
                    {!isView && <TableHead className="w-10" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isView
                    ? requisitionItems.map(item => {
                        const inv = inventoryMap[item.inventory_item_id];
                        return (
                          <TableRow key={item.req_item_id}>
                            <TableCell className="font-medium">{inv?.material_name ?? item.inventory_item_id}</TableCell>
                            <TableCell>{Number(item.requested_quantity)}</TableCell>
                            <TableCell>{inv?.unit_of_measure ?? '—'}</TableCell>
                            <TableCell className="text-muted-foreground">{item.notes ?? '—'}</TableCell>
                          </TableRow>
                        );
                      })
                    : lineItems.map((line, idx) => {
                        const selectedItem = inventoryMap[line.inventory_item_id];
                        return (
                          <TableRow key={idx}>
                            <TableCell>
                              <Select
                                value={line.inventory_item_id}
                                onValueChange={(v) => updateLine(idx, 'inventory_item_id', v)}
                              >
                                <SelectTrigger className="min-w-[200px]">
                                  <SelectValue placeholder="Select material" />
                                </SelectTrigger>
                                <SelectContent>
                                  {inventoryItems.filter(i => i.is_active).map(i => (
                                    <SelectItem key={i.inventory_item_id} value={i.inventory_item_id}>
                                      {i.material_name} ({Number(i.current_stock)} {i.unit_of_measure} avail.)
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={line.requested_quantity}
                                onChange={(e) => updateLine(idx, 'requested_quantity', e.target.value)}
                                placeholder="0"
                                className="w-24"
                                min="0"
                                step="0.001"
                              />
                              {errors[`qty_${idx}`] && <p className="text-xs text-destructive mt-1">{errors[`qty_${idx}`]}</p>}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {selectedItem?.unit_of_measure ?? '—'}
                            </TableCell>
                            <TableCell>
                              <Input
                                value={line.notes}
                                onChange={(e) => updateLine(idx, 'notes', e.target.value)}
                                placeholder="Optional notes"
                                className="text-sm"
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeLine(idx)}
                                disabled={lineItems.length === 1}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Notes</h4>
            </div>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any special instructions or requirements..."
              rows={3}
              readOnly={isView}
              className={isView ? 'bg-muted' : ''}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
            {isView ? 'Close' : 'Cancel'}
          </Button>
          {!isView && (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              <Send className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Submitting...' : 'Submit Requisition'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Confirm Action Dialog ────────────────────────────────────────────────────

interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description: string;
  actionLabel: string;
  destructive?: boolean;
  onConfirm: () => Promise<void>;
}

function ConfirmActionDialog({ open, onOpenChange, title, description, actionLabel, destructive, onConfirm }: ConfirmActionDialogProps) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try { await onConfirm(); onOpenChange(false); }
    finally { setLoading(false); }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="modal-content">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handle}
            disabled={loading}
            className={destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
          >
            {loading ? 'Processing...' : actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function MaterialRequisitions() {
  const { orgId } = useAuth();
  const { canWrite, canApprove, canDelete, canDo } = usePermission();
  const queryClient = useQueryClient();

  const { data: requisitions = [], isLoading } = useMaterialRequisitions(orgId);
  const { data: projects = [] } = useProjects(orgId);
  const { data: inventoryItems = [] } = useInventoryItems(orgId);
  const { data: warehouses = [] } = useWarehouses(orgId);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Modal state
  const [addOpen, setAddOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<MaterialRequisitionBackend | null>(null);
  const [viewItems, setViewItems] = useState<MaterialRequisitionItemBackend[]>([]);

  // Action confirm state
  type Action = 'approve' | 'reject' | 'cancel' | 'issue';
  const [pendingAction, setPendingAction] = useState<{ req: MaterialRequisitionBackend; action: Action } | null>(null);

  const refetch = () => queryClient.invalidateQueries({ queryKey: queryKeys.requisitions.all(orgId ?? '') });

  const handleViewDetails = async (req: MaterialRequisitionBackend) => {
    try {
      const items = await procurementService.getRequisitionItems(req.requisition_id);
      setViewItems(items);
      setViewTarget(req);
    } catch {
      setViewItems([]);
      setViewTarget(req);
    }
  };

  const executeAction = async (req: MaterialRequisitionBackend, action: Action) => {
    try {
      if (action === 'approve') await procurementService.approveMaterialRequisition(req.requisition_id);
      else if (action === 'reject') await procurementService.rejectMaterialRequisition(req.requisition_id);
      else if (action === 'cancel') await procurementService.cancelMaterialRequisition(req.requisition_id);
      else if (action === 'issue') await procurementService.issueMaterialRequisition(req.requisition_id);
      showSuccess({ title: 'Requisition Updated', message: `${req.requisition_number} has been ${action}d` });
      refetch();
    } catch (err: any) {
      showError({ title: 'Action Failed', message: err?.message ?? 'An error occurred' });
    }
  };

  const projectMap = useMemo(() =>
    Object.fromEntries(projects.map((p: any) => [p.project_id, p.name ?? p.project_name])),
    [projects]
  );

  const filteredRequisitions = useMemo(() => {
    return requisitions.filter(req => {
      const matchSearch =
        req.requisition_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (projectMap[req.project_id] ?? '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = filterStatus === 'all' || req.status.toLowerCase() === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [requisitions, searchTerm, filterStatus, projectMap]);

  const stats = useMemo(() => ({
    total: requisitions.length,
    pending: requisitions.filter(r => r.status === 'Pending').length,
    approved: requisitions.filter(r => r.status === 'Approved').length,
    issued: requisitions.filter(r => r.status === 'Issued').length,
  }), [requisitions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold">Material Requisitions</h2>
          <p className="text-muted-foreground">Request and track materials from inventory</p>
        </div>
        {canWrite('/material-requisitions') && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Requisition
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4 text-blue-600" />Total Requisitions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <p className="text-xs text-blue-600 mt-1">All time</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting review</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{stats.approved}</div>
            <p className="text-xs text-green-600 mt-1">Ready for issue</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Truck className="h-4 w-4 text-purple-600" />Issued
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{stats.issued}</div>
            <p className="text-xs text-purple-600 mt-1">Materials dispatched</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by requisition number or project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="issued">Issued</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon"><Filter className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Material Requisitions</CardTitle>
          <CardDescription>Showing {filteredRequisitions.length} of {requisitions.length} requisitions</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredRequisitions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ClipboardCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No requisitions found</p>
              <p className="text-sm mt-1">Create a requisition using the button above</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Requisition</TableHead>
                    <TableHead>Project</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequisitions.map(req => (
                    <TableRow key={req.requisition_id} className="hover:bg-muted/50">
                      <TableCell>
                        <p className="font-medium">{req.requisition_number}</p>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{projectMap[req.project_id] ?? '—'}</p>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {req.request_date && <p>Requested: {new Date(req.request_date).toLocaleDateString()}</p>}
                          {req.required_by_date && <p className="text-muted-foreground">Required: {new Date(req.required_by_date).toLocaleDateString()}</p>}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(req.status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewDetails(req)}>
                              <Eye className="h-4 w-4 mr-2" />View Details
                            </DropdownMenuItem>
                            {(canApprove('/material-requisitions') || canDo('approve_requisition')) && req.status === 'Pending' && (
                              <DropdownMenuItem onClick={() => setPendingAction({ req, action: 'approve' })}>
                                <CheckCircle className="h-4 w-4 mr-2" />Approve
                              </DropdownMenuItem>
                            )}
                            {req.status === 'Approved' && (
                              <DropdownMenuItem onClick={() => setPendingAction({ req, action: 'issue' })}>
                                <Truck className="h-4 w-4 mr-2" />Issue Materials
                              </DropdownMenuItem>
                            )}
                            {(req.status === 'Pending' || req.status === 'Approved') && (
                              <DropdownMenuItem
                                onClick={() => setPendingAction({ req, action: 'reject' })}
                                className="text-destructive"
                              >
                                <X className="h-4 w-4 mr-2" />Reject
                              </DropdownMenuItem>
                            )}
                            {req.status !== 'Issued' && req.status !== 'Rejected' && req.status !== 'Cancelled' && (
                              <DropdownMenuItem
                                onClick={() => setPendingAction({ req, action: 'cancel' })}
                                className="text-destructive"
                              >
                                <AlertTriangle className="h-4 w-4 mr-2" />Cancel
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals — rendered outside table */}
      {addOpen && (
        <RequisitionModal
          open={addOpen}
          onOpenChange={setAddOpen}
          mode="add"
          orgId={orgId!}
          projects={projects.map((p: any) => ({ project_id: p.project_id, name: p.name ?? p.project_name ?? '', project_manager_id: p.project_manager_id }))}
          inventoryItems={inventoryItems}
          warehouses={warehouses as any[]}
          onSuccess={refetch}
        />
      )}
      {viewTarget && (
        <RequisitionModal
          open={!!viewTarget}
          onOpenChange={(v) => { if (!v) { setViewTarget(null); setViewItems([]); } }}
          mode="view"
          requisition={viewTarget}
          requisitionItems={viewItems}
          orgId={orgId!}
          projects={projects.map((p: any) => ({ project_id: p.project_id, name: p.name ?? p.project_name ?? '', project_manager_id: p.project_manager_id }))}
          inventoryItems={inventoryItems}
          warehouses={warehouses as any[]}
          onSuccess={refetch}
        />
      )}
      {pendingAction && (
        <ConfirmActionDialog
          open={!!pendingAction}
          onOpenChange={(v) => { if (!v) setPendingAction(null); }}
          title={
            pendingAction.action === 'approve' ? 'Approve Requisition' :
            pendingAction.action === 'reject' ? 'Reject Requisition' :
            pendingAction.action === 'cancel' ? 'Cancel Requisition' :
            'Issue Materials'
          }
          description={
            pendingAction.action === 'approve'
              ? `Approve ${pendingAction.req.requisition_number}? This will mark it ready for material issue.`
              : pendingAction.action === 'reject'
              ? `Reject ${pendingAction.req.requisition_number}? This action cannot be undone.`
              : pendingAction.action === 'cancel'
              ? `Cancel ${pendingAction.req.requisition_number}? This action cannot be undone.`
              : `Issue all materials for ${pendingAction.req.requisition_number}? Stock levels will be reduced accordingly.`
          }
          actionLabel={
            pendingAction.action === 'approve' ? 'Approve' :
            pendingAction.action === 'reject' ? 'Reject' :
            pendingAction.action === 'cancel' ? 'Cancel Requisition' :
            'Issue Materials'
          }
          destructive={pendingAction.action === 'reject' || pendingAction.action === 'cancel'}
          onConfirm={() => executeAction(pendingAction.req, pendingAction.action)}
        />
      )}
    </div>
  );
}
